import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, ListTree, FileText, Newspaper, Video, Images, UserRound, Inbox, Settings, LogOut, ExternalLink, Calendar, BarChart3, Briefcase, Users, Megaphone, Briefcase as BriefcaseIcon, Sparkles, Palette } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAdmin } from "./AdminApp";
import { Switch } from "../../components/ui/switch";

const NAV = [
  ["", "Dashboard", LayoutDashboard], ["menu", "Menü", ListTree], ["sayfalar", "Sayfalar", FileText], ["haberler", "Yazılar", Newspaper],
  ["videolar", "Video Galerisi", Video], ["galeri", "Resim Galerisi", Images],
  ["calismalarim", "Çalışmalarım", Briefcase],
  ["profil", "Profil & Kampanya", UserRound],
  ["rehber", "Rehber", Users], ["reklam", "Reklam", Megaphone], ["istatistik", "İstatistikler", BarChart3], ["post-editor", "Post Editörü", Palette], ["ayarlar", "Ayarlar", Settings],
];

/**
 * Switch between the business/site admin and the private personal workspace.
 * Flipping ON navigates to /admin/workspace; flipping OFF comes back to
 * site management root. The switch is sticky (localStorage) so a hard refresh
 * on either side stays where the user was.
 */
function WorkspaceToggle() {
  const nav = useNavigate();
  const loc = useLocation();
  const inWorkspace = loc.pathname.startsWith("/admin/workspace");
  const flip = (v) => {
    if (v) nav("/admin/workspace");
    else nav("/admin");
  };
  return (
    <label className="flex items-center gap-3 text-sm" title="Yönetim ↔ Kişisel çalışma alanım">
      <span className={inWorkspace ? "text-stone-400" : "font-semibold text-[#14213D]"}>
        <BriefcaseIcon size={14} className="inline mr-1 -mt-0.5" />Site Management
      </span>
      <Switch checked={inWorkspace} onCheckedChange={flip} data-testid="admin-workspace-switch" />
      <span className={inWorkspace ? "font-semibold text-indigo-600" : "text-stone-400"}>
        <Sparkles size={14} className="inline mr-1 -mt-0.5" />My Workspace
      </span>
    </label>
  );
}

export default function Layout() {
  const { logout, user } = useAdmin();
  const { data: stats } = useQuery({ queryKey: ["admin", "stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });
  return (
    <div className="flex min-h-screen">
      <aside className="w-64 shrink-0 bg-[#14213D] text-white/80 flex-col hidden md:flex sticky top-0 h-screen" data-testid="admin-sidebar-nav">
        <div className="px-6 h-16 flex items-center font-signature text-2xl text-white leading-tight">WorkSpace &amp; Admin Panel</div>
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin/${to}`} end={to === ""} data-testid={`admin-nav-${to || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "hover:bg-white/5 hover:text-white"}`}>
              <Icon size={17} /> <span className="flex-1">{label}</span>
              {to === "mesajlar" && stats?.unread > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5" data-testid="unread-badge">{stats.unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/50 truncate">{user.email}</div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 sm:px-8 sticky top-0 z-30">
          <select className="md:hidden border rounded-md px-2 py-1 text-sm" onChange={(e) => (window.location.href = `/admin/${e.target.value}`)} data-testid="admin-mobile-nav">
            {NAV.map(([to, label]) => <option key={to} value={to}>{label}</option>)}
          </select>
          <WorkspaceToggle />
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-1.5 text-stone-600 hover:text-stone-900" data-testid="admin-view-site"><ExternalLink size={15} /> Siteyi gör</a>
            <button onClick={logout} className="text-sm flex items-center gap-1.5 text-stone-600 hover:text-red-600" data-testid="admin-logout"><LogOut size={15} /> Çıkış</button>
          </div>
        </header>
        <main className="p-4 sm:p-8 max-w-6xl"><Outlet /></main>
      </div>
    </div>
  );
}

export function PageTitle({ title, sub, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl font-bold" data-testid="admin-page-title">{title}</h1>
        {sub && <p className="text-stone-500 text-sm mt-1">{sub}</p>}
      </div>
      {action}
    </div>
  );
}
